"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { FaTimes, FaHeadset } from "react-icons/fa"
import { useInfoSitus } from "@/modules/situs/ui/info-situs-provider"
import { DynamicIcon } from "@/shared/components/dynamic-icon"
import { TeksBantuan } from "@/modules/help/ui/teks-bantuan"

export function SocialMediaButton() {
  const [isOpen, setIsOpen] = useState(false)
  const { kontak: kategori } = useInfoSitus()
  const [tabDipilih, setTabDipilih] = useState<string | null>(null)
  const [showBubble, setShowBubble] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const activeTab = tabDipilih ?? kategori[0]?.slug ?? ""

  useEffect(() => {
    const bubbleShowTimer = setTimeout(() => {
      if (!isOpen) setShowBubble(true)
    }, 3000)

    return () => clearTimeout(bubbleShowTimer)
  }, [isOpen])

  useEffect(() => {
    let bubbleHideTimer: NodeJS.Timeout
    if (showBubble) {
      bubbleHideTimer = setTimeout(() => {
        setShowBubble(false)
      }, 4500)
    }
    return () => clearTimeout(bubbleHideTimer)
  }, [showBubble])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false)
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      document.addEventListener("keydown", handleEscape)
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen])

  const tabData = kategori.map((kat) => ({ id: kat.slug, label: kat.nama, items: kat.items }))

  const activeItems = tabData.find((tab) => tab.id === activeTab)?.items || []

  return (
    <div ref={containerRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      <AnimatePresence>
        {showBubble && !isOpen && (
          <motion.button
            onClick={() => {
              setIsOpen(true)
              setShowBubble(false)
            }}
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="mb-3 px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-500 text-white text-xs font-semibold rounded-2xl shadow-[0_8px_24px_rgba(220,38,38,0.35)] relative mr-1 tracking-wide"
          >
            Ada kendala? Kami siap bantu
            <div className="absolute bottom-[-5px] right-5 w-2.5 h-2.5 bg-red-500 rotate-45" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            role="dialog"
            aria-label="Hubungi kami"
            className="mb-4 w-80 bg-popover rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.7)] border border-border overflow-hidden"
          >
            <div className="h-1 bg-gradient-to-r from-red-600 via-red-500 to-rose-500" />

            <div className="p-4 flex justify-between items-start bg-gradient-to-b from-red-950/25 to-transparent border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
                  <FaHeadset className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-foreground tracking-wide">Hubungi Kami</h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Pilih saluran yang kamu butuhkan</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Tutup"
                className="p-1.5 rounded-lg bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            </div>

            <div className="flex bg-secondary/60 p-1 mx-3 mt-3 rounded-xl border border-border">
              {tabData.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setTabDipilih(tab.id)}
                  className={`flex-1 py-1.5 text-[11px] font-semibold rounded-lg transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-red-500 text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-3 max-h-64 overflow-y-auto flex flex-col gap-2">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="flex flex-col gap-2"
                >
                  {activeItems.map((item) => (
                    <a
                      key={item.slug}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-2.5 rounded-xl border border-border bg-secondary/40 hover:bg-secondary/70 hover:border-primary/30 transition-all duration-200 group"
                    >
                      <div className="p-2 rounded-lg bg-red-500/10 text-primary group-hover:scale-105 group-hover:bg-red-500/15 transition-all">
                        <DynamicIcon iconType={item.iconType} iconValue={item.iconValue} warna={item.warna} className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-foreground tracking-wide">{item.nama}</div>
                        <div className="text-[10px] text-muted-foreground truncate mt-0.5">{item.deskripsi}</div>
                      </div>
                    </a>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="border-t border-border bg-secondary/40 px-4 py-3 text-center">
              <TeksBantuan pengantar="Butuh jawaban cepat? Kunjungi" tautan="Pusat Bantuan" onKlik={() => setIsOpen(false)} className="text-[11px]" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        aria-label={isOpen ? "Tutup menu kontak" : "Buka menu kontak"}
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen(!isOpen)
          if (showBubble) setShowBubble(false)
        }}
        className="w-14 h-14 rounded-full text-white shadow-[0_10px_30px_rgba(220,38,38,0.4)] flex items-center justify-center bg-gradient-to-br from-red-500 to-red-700"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        <div className="relative w-5 h-5 flex items-center justify-center">
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -60, opacity: 0, scale: 0.7 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: 60, opacity: 0, scale: 0.7 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute"
              >
                <FaTimes className="w-5 h-5 text-white" />
              </motion.div>
            ) : (
              <motion.div
                key="headset"
                initial={{ rotate: 60, opacity: 0, scale: 0.7 }}
                animate={{ rotate: 0, opacity: 1, scale: 1 }}
                exit={{ rotate: -60, opacity: 0, scale: 0.7 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute flex items-center justify-center"
              >
                <FaHeadset className="w-5 h-5 text-white" />
                <span className="absolute -top-1.5 -right-1.5 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.button>
    </div>
  )
}
